import React, { useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash/debounce';
import {
	Card,
	InputAdornment,
	LinearProgress,
	Slider,
	TextField,
	Typography,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import DownArrow from '@material-ui/icons/ArrowDropDown';
import UpArrow from '@material-ui/icons/ArrowDropUp';
import { confidenceChangeFunc, targetPriceChangeFunc } from '../../Shared';
import TickerStats from './TickerStats';
import { setAppStateAction } from '../../actions/appStateAction';
import styles from './OptionsBoardStyles';

const TickerStatsHeader = () => {
	const dispatch = useDispatch();
	const { appReducer, optionChainReducer } = useSelector((state) => state);

	const {
		isMobile,
		targetPrice,
		confidence,
		skew,
		imp_vol,
		quoteInfo: { latestPrice },
		ticker,
	} = appReducer;

	const { transformScale } = optionChainReducer;
	const setAppState = (payload) => dispatch(setAppStateAction(payload));

	const [inputValue, setInputValue] = useState(targetPrice);

	const debouncedTargetPriceChange = useCallback(
		debounce(async (value) => {
			if (value < 0) return;
			await targetPriceChangeFunc(value, appReducer, setAppState);
		}, 1000), // 1000ms = 1 second delay
		[]
	);

	const prevTickerRef = useRef();
	// prevTickerRef.current = ticker;
	useEffect(() => {
		if (targetPrice == 0 && prevTickerRef.current !== ticker) {
			setInputValue(0);
		}
		prevTickerRef.current = ticker;
	}, [targetPrice]);

	useEffect(() => {
		if (inputValue !== targetPrice) {
			debouncedTargetPriceChange(inputValue);
		}
	}, [inputValue, debouncedTargetPriceChange, targetPrice]);

	const handleTargetPriceChange = (event) => {
		setInputValue(event.target.value);
	};

	const targetChange = targetPrice - latestPrice;
	const targetChangePercent = targetPrice / latestPrice - 1;
	const absTargChangePerc = Math.abs(targetChangePercent * 100);

	return (
		<div
			style={
				isMobile
					? styles.headerInfoMobile
					: {
							...styles.headerInfo,
							transform: `scale(${transformScale})`,
					  }
			}
			className='sticky'
		>
			<TickerStats />

			<Card style={isMobile ? styles.selectorsMobile : styles.selectors}>
				<div style={styles.selectorsFlex}>
					<div style={styles.selectorText}>
						Target {!isMobile && `Price`}
						{targetPrice && targetPrice !== 0 ? (
							<span
								style={
									targetChange < 0
										? styles.negPriceChange
										: styles.posPriceChange
								}
							>
								{targetChange < 0 ? (
									<DownArrow style={styles.downArrow} />
								) : (
									<UpArrow style={styles.upArrow} />
								)}{' '}
								{absTargChangePerc >= 100
									? absTargChangePerc.toFixed(0)
									: absTargChangePerc >= 10
									? absTargChangePerc.toFixed(1)
									: absTargChangePerc.toFixed(2)}
								%
							</span>
						) : (
							<span style={styles.posPriceChange} />
						)}
					</div>
					<TextField
						name='targetPrice'
						autoFocus
						style={styles.inputItem}
						type='number'
						step={0.01}
						min={latestPrice * 0.75}
						max={latestPrice * 1.25}
						value={inputValue}
						InputProps={{
							style: {
								textAlign: 'center',
								fontSize: isMobile ? 16 : 14,
							},
							startAdornment: (
								<InputAdornment position='start'>$</InputAdornment>
							),
						}}
						onChange={handleTargetPriceChange}
					/>
				</div>
				<div style={styles.selectorsFlex}>
					<Typography
						variant='inherit'
						component='h3'
						style={styles.selectorText}
					>
						Confidence ({(confidence * 10).toFixed(0)}
						%)
					</Typography>
					<Slider
						style={styles.sliderItem}
						name='confidence'
						value={confidence.toFixed(1) * 1}
						step={0.1}
						min={0}
						max={10}
						onChange={(event, value) =>
							confidenceChangeFunc(value, appReducer, setAppState)
						}
					/>
				</div>
			</Card>

			{!isMobile && (
				<Card style={styles.thirdSelector}>
					<div style={styles.selectorsFlexCenter}>
						<Typography
							variant='inherit'
							component='h3'
							style={styles.selectorText}
						>
							3M Sentiment
						</Typography>
						<div style={styles.progressBarContainer}>
							<Typography
								variant='body1'
								component='span'
								style={styles.progressLabelTextLeft}
							>
								Bull
							</Typography>
							<LinearProgress
								value={skew * 1}
								variant='determinate'
								color='secondary'
								style={styles.progressBar}
							/>
							<Typography
								variant='body1'
								component='span'
								style={styles.progressLabelTextRight}
							>
								Bear
							</Typography>
						</div>
					</div>
					<div style={styles.selectorsFlexCenter}>
						<Typography
							variant='inherit'
							component='h3'
							style={styles.selectorText}
						>
							1M ATM IV{' '}
						</Typography>

						<div style={styles.progressBarIVContainer}>
							<Typography
								variant='body1'
								component='span'
								style={styles.progressLabelTextLeft}
							>
								High
							</Typography>
							<LinearProgress
								value={100 - imp_vol * 1}
								variant='determinate'
								color='secondary'
								style={styles.progressBar}
							/>
							<Typography
								variant='body1'
								component='span'
								style={styles.progressLabelTextRight}
							>
								{' '}
								Low
							</Typography>
						</div>
					</div>
				</Card>
			)}
		</div>
	);
};

export default React.memo(TickerStatsHeader);



// WEBPACK FOOTER //
// ./src/Pages/OptionsBoard/TickerStatsHeader.jsx